import type { UpdateAppointmentTypeInput } from 'app/generated/gql/graphql'
import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { graphql } from 'app/generated/gql'
import { EditAppointmentTypeModal } from 'app/components/scheduling/EditAppointmentTypeModal'
import { AIAgentConfigurationType } from 'app/types/entities'
import { AppointmentTypeType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'

import { useAppointmentTypeStore } from 'app/stores/StoreContext'
import { opsClient } from 'app/lib/opsUrqlClient'
import { logError } from 'lib/logging'

// aiAgentConfiguration lives on the ops-only GraphQL schema, so it must be read through
// opsClient, not the default public client.
const AI_AGENT_CONFIG_QUERY = graphql(`
  query AiAgentConfigForApptTypes($locationId: ID!) {
    aiAgentConfiguration(locationId: $locationId) {
      id
      locationId
      generalApptId
      emergencyApptId
      consultApptId
      limitedExamApptId
    }
  }
`)

function sortByActiveAgent(items: AppointmentTypeType[]): AppointmentTypeType[] {
  const enabled = items.filter((item) => item.activeAgent)
  const disabled = items.filter((item) => !item.activeAgent)
  return [...enabled, ...disabled]
}

function deriveExcludableAppointmentTypes(items: AppointmentTypeType[]): { id: string; name: string }[] {
  return items.map((apt) => ({
    id: `appointmenttypes/${apt.vendorId}`,
    name: (apt.alias || apt.displayName || apt.name) as string,
  }))
}

export type UseAiAgentAppointmentTypesReturn = {
  rows: AppointmentTypeType[]
  agentConfigState: AIAgentConfigurationType | undefined
  isSyncing: boolean
  excludableAppointmentTypes: { id: string; name: string }[]
  onToggleActive: (id: string, active: boolean) => Promise<void>
  onAppointmentTypeUpdated: (appointmentType: AppointmentTypeType) => void
  openEditAppointmentTypeModal: (appointmentType: AppointmentTypeType) => Promise<void>
  handleRefreshClick: () => Promise<void>
  handleAppointmentCategoryChange: (appointmentTypeId: string, categories: string[]) => void
}

export function useAiAgentAppointmentTypes(): UseAiAgentAppointmentTypesReturn {
  const { locationId } = useParams()
  const appointmentTypeStore = useAppointmentTypeStore()
  const [rows, setRows] = useState<AppointmentTypeType[]>([])
  const [excludableAppointmentTypes, setExcludableAppointmentTypes] = useState<{ id: string; name: string }[]>([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [agentConfigState, setAgentConfigState] = useState<AIAgentConfigurationType | undefined>()

  useEffect(() => {
    if (!locationId) return
    // Appointment types come from the public scheduling endpoint; agentConfig (which drives the
    // category column) comes from the authenticated ops GraphQL schema.
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => {
        const types = (data.appointmentTypes ?? []) as AppointmentTypeType[]
        setRows(sortByActiveAgent(types))
        setExcludableAppointmentTypes(deriveExcludableAppointmentTypes(types))
      })
      .catch((err) => logError(`Error fetching AI agent appointment types`, err))

    opsClient
      .query(AI_AGENT_CONFIG_QUERY, { locationId })
      .toPromise()
      .then((result) => {
        if (result.error) {
          logError(`Error fetching AI agent configuration`, result.error)
          return
        }
        setAgentConfigState(result.data?.aiAgentConfiguration ?? undefined)
      })
  }, [locationId])

  const onToggleActive = async (id: string, active: boolean) => {
    const result = await appointmentTypeStore.update(id, { activeAgent: active })
    if (!result.error) {
      notifySuccess(`Appointment type status saved`)
      setRows((prev) => sortByActiveAgent(prev.map((row) => (row.id === id ? { ...row, activeAgent: active } : row))))
    } else {
      notifyError(`Error saving appointment type status`)
    }
  }

  const onAppointmentTypeUpdated = (appointmentType: AppointmentTypeType) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id === appointmentType.id) {
          return appointmentType
        }
        return row
      }),
    )
  }

  const handleSaveAppointmentType = async (id: string, input: UpdateAppointmentTypeInput) => {
    const result = await appointmentTypeStore.update(id, input)
    if (result.error) return undefined
    const data = result.data as { updateAppointmentType?: AppointmentTypeType } | undefined
    return data?.updateAppointmentType
  }

  const openEditAppointmentTypeModal = async (appointmentType: AppointmentTypeType) => {
    showModal(
      <EditAppointmentTypeModal
        onAppointmentTypeUpdated={onAppointmentTypeUpdated}
        appointmentType={appointmentType}
        excludableAppointmentTypes={excludableAppointmentTypes}
        onSave={handleSaveAppointmentType}
      />,
      {
        title: `Edit Appointment type`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
    )
  }

  const handleRefreshClick = async () => {
    setIsSyncing(true)
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `sync` }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        setIsSyncing(false)
        if (data?.action === `sync` && data.success) {
          const added = data.added as number
          const deleted = data.deleted as number
          notifySuccess(
            added
              ? `${added} New Appointment Types Added`
              : `No New Appointment Types Added` + (deleted ? `,\n ${deleted} Appointment Types Deleted` : ``),
          )
          if (data.appointmentTypes) {
            const types = data.appointmentTypes as AppointmentTypeType[]
            setRows(sortByActiveAgent(types))
            setExcludableAppointmentTypes(deriveExcludableAppointmentTypes(types))
          }
        } else {
          notifyError(`Error syncing appointment types`)
        }
      })
      .catch((err) => {
        setIsSyncing(false)
        notifyError(`Error syncing appointment types`)
        logError(`Error syncing appointment types`, err)
      })
  }

  const handleAppointmentCategoryChange = (appointmentTypeId: string, categories: string[]) => {
    fetch(`/api/scheduling/locations/${locationId}/appointment-types`, {
      method: `POST`,
      credentials: `include`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify({ action: `map-appointment-type`, appointmentTypeId, categories }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        if (data?.success) {
          // The map response is active-only (TRK-5787). Rebuilding the list from it dropped
          // disabled types until a refresh (TRK-5835), so apply only agentConfig and keep
          // the full list already loaded from the scheduling endpoint.
          if (data.agentConfig) setAgentConfigState(data.agentConfig as AIAgentConfigurationType)
          notifySuccess(`Appointment type mapped successfully`)
        } else {
          notifyError(`Error mapping appointment type`)
        }
      })
      .catch((err) => {
        notifyError(`Error mapping appointment type`)
        logError(`Error mapping appointment type`, err)
      })
  }

  return {
    rows,
    agentConfigState,
    isSyncing,
    excludableAppointmentTypes,
    onToggleActive,
    onAppointmentTypeUpdated,
    openEditAppointmentTypeModal,
    handleRefreshClick,
    handleAppointmentCategoryChange,
  }
}
